import { api } from '../../../shared/api';
import type { User, LoginCredentials, SignupCredentials } from '../types';

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
  };
}

interface UserResponse {
  success: boolean;
  data: {
    user: User;
  };
}

export const authApi = {
  // Get current user from backend
  getCurrentUser: async (): Promise<User | null> => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return null;

      const response = await api.get<UserResponse>('/auth/me');
      const user = response.data.user;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    } catch {
      // Clear invalid tokens
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },

  // Login user
  login: async (credentials: LoginCredentials): Promise<User> => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      const { user, tokens } = response.data;

      // Store auth data
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return user;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Login failed. Please check your credentials.');
    }
  },

  // Signup user
  signup: async (credentials: SignupCredentials): Promise<User> => {
    try {
      const response = await api.post<AuthResponse>(
        '/auth/signup',
        credentials,
      );

      const { user, tokens } = response.data;

      // Store auth data
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return user;
    } catch (error) {
      console.log('Auth error', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Signup failed. Please try again.');
    }
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      // Server reads refresh token from cookie and body; send body only if present
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      await api.post(
        '/auth/logout',
        refreshToken ? { refreshToken } : undefined,
      );
    } catch {
      // Continue with logout even if API call fails
      console.error('Logout API error');
    } finally {
      // Always clear local storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  },

  // Refresh token
  refreshToken: async (): Promise<{
    accessToken: string;
    refreshToken: string;
  }> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await api.post<{
        success: boolean;
        data: {
          tokens: {
            accessToken: string;
            refreshToken: string;
          };
        };
      }>('/auth/refresh-token', { refreshToken });

      const { tokens } = response.data;

      // Update stored tokens
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);

      return tokens;
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || 'Token refresh failed';
      throw new Error(message);
    }
  },

  // Google OAuth
  googleAuth: async (idToken: string): Promise<User> => {
    try {
      console.log('[AuthAPI] Sending Google auth request to backend...');
      const response = await api.post<AuthResponse>('/auth/google', {
        idToken,
      });
      console.log('[AuthAPI] Google auth response received:', response.success);

      const { user, tokens } = response.data;

      // Store auth data
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      console.log('[AuthAPI] Google auth successful, user stored');
      return user;
    } catch (error: unknown) {
      console.error('[AuthAPI] Google auth error:', error);
      const err =
        (error as { response?: { data?: { message?: string } } }) || {};
      const message =
        err.response?.data?.message || 'Google authentication failed';
      throw new Error(message);
    }
  },

  // Google OAuth code fallback
  googleAuthWithCode: async (code: string): Promise<User> => {
    try {
      const response = await api.post<AuthResponse>('/auth/google/code', {
        code,
      });
      const { user, tokens } = response.data;
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      const message =
        err.response?.data?.message || 'Google (code) authentication failed';
      throw new Error(message);
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);
    return !!(token && user);
  },

  // Get stored user data
  getStoredUser: (): User | null => {
    try {
      const userData = localStorage.getItem(USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },
};
